import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FadeInUp, SectionLabelLight, AccentLineLight } from './AnimationWrappers';
import { ArrowUpRight } from 'lucide-react';

const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  /* Animated grid dots background */
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    const dots = [];
    const spacing = 40;
    const init = () => {
      dots.length = 0;
      for (let x = 0; x < canvas.width; x += spacing) {
        for (let y = 0; y < canvas.height; y += spacing) {
          dots.push({ x, y, baseAlpha: Math.random() * 0.08 + 0.02 });
        }
      }
    };
    init();

    let time = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.003;
      dots.forEach(d => {
        const wave = Math.sin(d.x * 0.008 + time) * Math.cos(d.y * 0.008 + time * 0.7);
        const alpha = d.baseAlpha + wave * 0.04;
        ctx.fillStyle = `rgba(200, 16, 46, ${Math.max(0, alpha)})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1, 0, Math.PI * 2);
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  const words = ['Idea', 'Tech Pack', 'Prototype', 'Production'];

  return (
    <section
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      className="relative overflow-hidden"
      style={{ background: '#1C1C1E', padding: '70px 0 0' }}
    >
      {/* Animated dot grid */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.6 }}
      />

      {/* Diagonal accent */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: 0, right: '-10%', width: '1px', height: '140%',
          background: 'rgba(200,16,46,0.12)',
          transform: 'rotate(25deg)', transformOrigin: 'top right',
        }}
      />

      <div className="container-main relative z-10">
        <SectionLabelLight number="11" title="Let's Work Together" />
        <FadeInUp>
          <AccentLineLight />
        </FadeInUp>

        {/* Main content */}
        <div className="max-w-4xl">
          <FadeInUp>
            <h2
              className="font-display text-white mb-6"
              style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.15 }}
            >
              Every Great Product Starts<br />
              With a <span style={{ color: '#C8102E' }}>Conversation</span>.
            </h2>
          </FadeInUp>

          <FadeInUp delay={0.1}>
            <p
              className="font-body mb-10"
              style={{
                fontSize: '1.05rem', fontWeight: 300,
                color: 'rgba(255,255,255,0.55)', lineHeight: 1.8,
                maxWidth: 580,
              }}
            >
              Whether you're a global brand planning your next collection, a startup
              bringing a new label to life, or an organisation sourcing at scale —
              we'd love to hear what you're building.
            </p>
          </FadeInUp>

          {/* Journey words */}
          <FadeInUp delay={0.15}>
            <div className="flex items-center gap-3 sm:gap-4 mb-14 flex-wrap">
              {words.map((word, i) => (
                <React.Fragment key={word}>
                  <motion.span
                    initial={{ opacity: 0, y: 8 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                    className="font-display"
                    style={{
                      fontSize: 'clamp(0.85rem, 1.5vw, 1.1rem)',
                      fontWeight: 600,
                      color: i === words.length - 1 ? '#C8102E' : 'rgba(255,255,255,0.35)',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {word}
                  </motion.span>
                  {i < words.length - 1 && (
                    <motion.span
                      initial={{ opacity: 0, scaleX: 0 }}
                      animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
                      transition={{ delay: 0.35 + i * 0.1, duration: 0.3 }}
                      style={{
                        width: 'clamp(20px, 3vw, 40px)',
                        height: '1px',
                        background: 'rgba(255,255,255,0.12)',
                        display: 'block',
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </FadeInUp>
        </div>

        {/* ── EMAIL HERO BLOCK ── */}
        <FadeInUp delay={0.2}>
          <div
            className="relative"
            style={{
              borderTop: '1px solid rgba(255,255,255,0.06)',
              paddingTop: '3rem',
              paddingBottom: '1rem',
            }}
          >
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="font-mono block mb-4"
              style={{
                fontSize: '0.6rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#C8102E',
              }}
            >
              Write to us
            </motion.span>

            <a
              href="mailto:info@qualiance.com"
              data-testid="contact-email-link"
              className="group inline-flex items-center gap-3 sm:gap-5 transition-all duration-500"
              style={{ textDecoration: 'none' }}
            >
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.55, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="font-display text-white inline-block transition-colors duration-300 group-hover:text-[#C8102E]"
                style={{
                  fontSize: 'clamp(1.6rem, 5vw, 3.6rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}
              >
                info@qualiance.com
              </motion.span>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.7, duration: 0.4 }}
                className="shrink-0 flex items-center justify-center rounded-full transition-all duration-300 group-hover:scale-110"
                style={{
                  width: 'clamp(36px, 4vw, 52px)',
                  height: 'clamp(36px, 4vw, 52px)',
                  background: 'rgba(200,16,46,0.12)',
                  border: '1px solid rgba(200,16,46,0.25)',
                }}
              >
                <ArrowUpRight
                  size={20}
                  color="#C8102E"
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </motion.div>
            </a>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              style={{
                height: 2,
                background: 'linear-gradient(90deg, #C8102E 0%, rgba(200,16,46,0) 100%)',
                marginTop: 12,
                transformOrigin: 'left',
                maxWidth: 400,
              }}
            />
          </div>
        </FadeInUp>
      </div>

      <div style={{ height: '60px' }} />
    </section>
  );
};

export default ContactSection;
